import React, { useState, Fragment, useEffect, FC } from 'react';
import { Button, InputHelperText } from 'react-admin';
import { useForm } from 'react-final-form';
import { useInput } from 'ra-core';
import { InputLabel, MenuItem, FormControl, Select, FormHelperText } from '@material-ui/core';
import lodashGet from 'lodash/get';
import makeStyles from '@material-ui/core/styles/makeStyles';
import cx from 'classnames';
import { useTranslate } from 'react-admin';
import onError from '~/errorsHandler';
import { AccountTableMultiselectProps } from '~/types/AccountTableMultiselect';
import { useGetAllAccounts } from '~/hooks/accounts';
import { Account } from '~/types';
import { ACCOUNTS_PRODUCT_CATEGORIES } from '~/utils/constants/accounts';
import AccountsTableDialog from './AccountsTableDialog';

const useStyles = makeStyles(() => ({
  fullWidth: {
    width: '100%',
  },
  selectInput: {
    marginBottom: 3,
  },
}));

const AccountTableMultiselect: FC<AccountTableMultiselectProps> = (props) => {
  const {
    source = '',
    label,
    optionText,
    children,
    validate,
    fullWidth,
    tableProps,
    warning,
    title = label,
    filters,
    resource,
    profileId,
    solutionId,
    terminalRecord,
    ...rest
  } = props;
  const form = useForm();
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string =>
    translate(`components.ra.fields.AccountTableMultiselect.${key}`);
  const tt = (key: string): string => translate(`resources.businesses.terminals.${key}`);

  const {
    input,
    isRequired,
    meta: { error, touched },
  } = useInput({ source, validate, ...rest });

  const initialSelected = terminalRecord?.accountIds || [];
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [selectedItems, setSelectedItems] = useState<Account[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSelected(input?.value || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value]);

  const handleToggleSelect = (item: Account) => {
    const selectedArr = Array.isArray(selected) ? selected : [];
    const set = new Set(selectedArr);
    let newSelected: string[];
    let newSelectedItems: Account[];

    if (set.has(item.id)) {
      set.delete(item.id);
      newSelected = Array.from(set);
      newSelectedItems = selectedItems.filter((acc) => acc.id !== item.id);
    } else {
      set.add(item.id);
      newSelected = Array.from(set);
      newSelectedItems = [...selectedItems, item];
    }

    setSelected(newSelected);
    setSelectedItems(newSelectedItems);
  };

  const isSelected = (item: string) => selected.indexOf(item) !== -1;

  const { data: selectedDataInit, isLoading: isLoadingSelectedData } = useGetAllAccounts(
    {
      resource,
      filters: { excludedProductCategories: ACCOUNTS_PRODUCT_CATEGORIES, ids: selected },
    },
    {
      enabled: !!profileId && initialSelected?.length > 0,
      onSuccess: (data) => {
        setSelectedItems(data);
      },
    }
  );

  const hasSelection = Array.isArray(selected) && selected.length > 0;

  const handleDeselectAll = () => {
    setSelected([]);
    setSelectedItems([]);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onDialogCancel = () => {
    setSelected(initialSelected);
    setSelectedItems(selectedDataInit ?? []);
    handleClose();
    form.change(source, initialSelected ?? []);
  };

  return (
    <Fragment>
      <FormControl
        className={cx(classes.selectInput, { [classes.fullWidth]: fullWidth })}
        onClick={() => setOpen(true)}
      >
        <InputLabel required={isRequired} variant='filled'>
          {label}
        </InputLabel>
        <Select
          value={isLoadingSelectedData ? [] : selectedItems?.map((item: Account) => item.id) ?? []}
          onChange={input.onChange}
          label={label}
          MenuProps={{ open: false }}
          open={false}
          error={!!(touched && error)}
          multiple
          disabled={isLoadingSelectedData}
        >
          {isLoadingSelectedData
            ? []
            : selectedItems?.map((selectedValueItem: Account) => (
                <MenuItem key={selectedValueItem.id} value={selectedValueItem.id}>
                  {lodashGet(selectedValueItem, optionText)}
                </MenuItem>
              ))}
        </Select>
        <FormHelperText error={!!(touched && error)}>
          {touched && error ? (
            <InputHelperText touched={touched} error={error} />
          ) : (
            <span>&#8203;</span>
          )}
        </FormHelperText>
      </FormControl>
      <AccountsTableDialog
        resource={resource}
        profileId={profileId}
        solutionId={solutionId}
        open={open}
        onClose={onDialogCancel}
        title={title}
        warning={warning}
        filters={filters}
        tableProps={tableProps}
        optionText={optionText}
        children={children}
        isSelected={isSelected}
        handleToggleSelect={handleToggleSelect}
        hasSelection={hasSelection}
        handleDeselectAll={handleDeselectAll}
        selectedItems={selectedItems}
        handleDeselect={(id) => handleToggleSelect(selectedItems.find((a) => a.id === id)!)}
        renderActions={() => (
          <>
            <Button label={t('cancel')} onClick={onDialogCancel} />
            <Button
              label={t('ok')}
              onClick={() => {
                const checkBeforeOk = beforeOkCheck(selectedItems);
                if (!checkBeforeOk) {
                  onError({ body: { message: tt('accountsCurrencyError') } });
                  return;
                }
                form.change(source, selected);
                handleClose();
              }}
            />
          </>
        )}
      />
    </Fragment>
  );
};

const beforeOkCheck = (selected: Account[]) => {
  if (!Array.isArray(selected)) return false;
  const currencies = selected.map((acc) => acc.currency);
  return currencies.length === new Set(currencies).size;
};

export default AccountTableMultiselect;
